import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Center,
  Badge
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

interface LocationState {
  registry?: any;
  mealDetails?: any;
  selectedDate?: string;
  contactInfo?: any;
}

const SignUpSuccess = () => {
  const { registryId } = useParams<{ registryId: string }>();
  const navigate = useNavigate();
  
  // Store signup data in component state instead of relying on sessionStorage
  const [signupData, setSignupData] = useState<any>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Load data from sessionStorage once and store in component state
  React.useEffect(() => {
    const signupDataString = sessionStorage.getItem('signupData');
    if (signupDataString && !dataLoaded) {
      try {
        const parsedData = JSON.parse(signupDataString);
        setSignupData(parsedData);
        setDataLoaded(true);
        
        // Only remove from sessionStorage after successfully loading into state
        sessionStorage.removeItem('signupData');
      } catch (error) {
        console.error('Error parsing signup data:', error);
        setDataLoaded(true); // Mark as loaded even if parsing failed
      }
    } else if (!signupDataString) {
      setDataLoaded(true); // No data to load
    }
  }, [dataLoaded]);
  
  // Use component state instead of sessionStorage
  const registry = signupData?.registry;
  const mealDetails = signupData?.mealDetails;
  const selectedDate = signupData?.selectedDate;
  const contactInfo = signupData?.contactInfo;

  const handleBackToRegistry = () => {
    navigate(`/registry/${registryId}`);
  };

  const handleSignUpAnother = () => {
    navigate(`/registry/${registryId}`);
  };

  // Format date for display (same function as in RegistryView)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const trackAdClick = async (adName: string, url: string) => {
    try {
      await fetch('https://xxkwmtffbeihotf7j657zatiwe0lroyx.lambda-url.us-east-2.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adName }),
      });
    } catch (error) {
      console.error('Failed to track ad click:', error);
    }
    
    // Open the link regardless of tracking success
    window.open(url, '_blank');
  };

  const formatCouponCode = (code: string) => {
    if (!code || code.length !== 6) return code;
    return `${code.slice(0, 3)}-${code.slice(3)}`;
  };

  return (
    <Box 
      bg="mamaFuel.cream" 
      width="100%" 
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
    >
      <Container maxW="container.lg" width="100%">
        {/* Success Message */}
        <Box 
          borderWidth="1px" 
          borderRadius="lg" 
          bg="white" 
          shadow="md" 
          p={8}
          textAlign="center"
          mb={8}
        >
          <VStack spacing={6}>
            <Box
              bg="green.100"
              borderRadius="full"
              p={4}
              display="inline-flex"
            >
              <CheckIcon color="green.500" boxSize={8} />
            </Box>
            
            <Heading size="lg" color="green.600">
              Meal Signup Successful!
            </Heading>
            
            <Text fontSize="lg" maxW="600px">
              Thank you for signing up to bring a meal! You'll receive a confirmation email shortly with all the details. 
            </Text>

            {/* Show signup details if available */}
            {registry && mealDetails && selectedDate && (
              <VStack spacing={4} align="stretch">
                <Box 
                  bg="blue.50" 
                  p={4} 
                  borderRadius="md" 
                  maxW="600px"
                  textAlign="left"
                >
                  <Text fontSize="sm" fontWeight="bold" mb={2}>Your Meal Signup Summary:</Text>
                  <Text fontSize="sm">
                    <strong>Meal:</strong> {mealDetails.mealName}
                  </Text>
                  <Text fontSize="sm">
                    <strong>When:</strong> {formatDate(selectedDate)}
                  </Text>
                  {registry.address?.street && (
                    <Text fontSize="sm">
                      <strong>Where:</strong> {registry.address?.street}, {registry.address?.city}, {registry.address?.state} {registry.address?.zip}
                    </Text>
                  )}
                  {contactInfo && (
                    <Text fontSize="sm">
                      <strong>Contact:</strong> {contactInfo.name} ({contactInfo.email})
                    </Text>
                  )}
                </Box>

                {/* Coupon Code Section */}
                {signupData?.couponCode && signupData?.selectedFoodSubscription && (
                  <Box 
                    bg="green.50" 
                    p={4} 
                    borderRadius="md" 
                    maxW="600px"
                    textAlign="left"
                    borderWidth="2px"
                    borderColor="green.200"
                  >
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={2} align="center">
                        <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                          🎉 MommaFuel Promotion Code
                        </Badge>
                      </HStack>
                      
                      <Box 
                        bg="white" 
                        p={3}
                        borderRadius="md" 
                        textAlign="center"
                        borderWidth="2px"
                        borderColor="green.300"
                      >
                        <Text fontSize="2xl" fontWeight="bold" color="green.700">
                          {formatCouponCode(signupData.couponCode)}
                        </Text>
                      </Box>
                      
                      <Text fontSize="md" fontWeight="bold">
                        {signupData.selectedFoodSubscription.name}
                      </Text>
                      
                      {signupData.selectedFoodSubscription.promotion && signupData.selectedFoodSubscription.promotion.length > 0 && (
                        <Box>
                          {signupData.selectedFoodSubscription.promotion.map((promo: string, index: number) => (
                            <Box
                              key={index}
                              bg="red.300"
                              color="black"
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontSize="sm"
                              fontWeight="bold"
                              border="1px solid black"
                              mb={1}
                              display="inline-block"
                            >
                              🏷️ {promo}
                            </Box>
                          ))}
                        </Box>
                      )}
                      
                      {signupData.selectedFoodSubscription.claimInstructions && (
                        <Box>
                          <Text fontSize="sm" fontWeight="bold" mb={1}>How to use your promotion:</Text>
                          <Text fontSize="sm">{signupData.selectedFoodSubscription.claimInstructions}</Text>
                        </Box>
                      )}
                      
                      {signupData.selectedFoodSubscription.address && (
                        <Text fontSize="sm">
                          <strong>📍 Location:</strong> {signupData.selectedFoodSubscription.address.street}, {signupData.selectedFoodSubscription.address.city}
                        </Text>
                      )}
                      
                      {(signupData.selectedFoodSubscription.webLink || signupData.selectedFoodSubscription.phoneNumber) && (
                        <HStack spacing={4}>
                          {signupData.selectedFoodSubscription.webLink && (
                            <Text
                              as="a"
                              href={signupData.selectedFoodSubscription.webLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              fontSize="sm"
                              color="blue.600"
                              textDecoration="underline"
                              _hover={{ color: "blue.800" }}
                            >
                              🔗 {signupData.selectedFoodSubscription.name}
                            </Text>
                          )}
                          {signupData.selectedFoodSubscription.phoneNumber && (
                            <Text fontSize="sm" color="gray.700">
                              📞 {signupData.selectedFoodSubscription.phoneNumber}
                            </Text>
                          )}
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}

            {/* Action Buttons */}
            <Stack 
              direction={{ base: "column", md: "row" }} 
              spacing={4} 
              mt={2}
              width="100%"
              justify="center"
            >
              <Button
                size="lg"
                onClick={handleBackToRegistry}
                minHeight="48px"
                whiteSpace="nowrap"
                px={6}
                py={3}
                fontSize={{ base: "sm", md: "lg" }}
              >
                Return to {registry?.parentNames ? `${registry.parentNames}'s` : ""} MommaFuel
              </Button>
              
              <Button
                bg="mamaFuel.moss.700"
                size="lg"
                onClick={handleSignUpAnother}
                minHeight="48px"
                whiteSpace="nowrap"
                px={6}
                py={3}
              >
                Sign Up to Bring Another Meal
              </Button>
            </Stack>
          </VStack>
        </Box>

        {/* Sponsored Advertisements Section */}
        <Box 
          borderWidth="1px" 
          borderRadius="lg" 
          bg="white" 
          shadow="md" 
          p={6}
        >
          <VStack spacing={6}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} width="100%">
              {/* Edible Arrangements Ad */}
              <Box 
                borderWidth="1px" 
                borderRadius="md" 
                p={4} 
                textAlign="center"
                bg="white"
                _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => trackAdClick('edible-arrangements', 'https://www.ediblearrangements.com')}
              >
                <VStack spacing={3}>
                  <Box 
                    bg="white" 
                    borderRadius="md" 
                    p={3} 
                    width="200px" 
                    height="200px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image 
                      src="/edible-arrangements-logo.png"
                      alt="Edible Arrangements"
                      maxHeight="240px"
                      maxWidth="200px"
                      objectFit="contain"
                    />
                  </Box>
                </VStack>
              </Box>

              {/* DoorDash Ad */}
              <Box 
                borderWidth="1px" 
                borderRadius="md" 
                p={4} 
                textAlign="center"
                bg="white"
                _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => trackAdClick('doordash', 'https://www.doordash.com')}
              >
                <VStack spacing={3}>
                  <Box 
                    bg="white" 
                    borderRadius="md" 
                    p={3} 
                    width="200px" 
                    height="200px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image 
                      src="/doordash.png"
                      alt="DoorDash"
                      maxHeight="240px"
                      maxWidth="200px"
                      objectFit="contain"
                    />
                  </Box>
                </VStack>
              </Box>

              {/* Starbucks Ad */}
              <Box 
                borderWidth="1px" 
                borderRadius="md" 
                p={4} 
                textAlign="center"
                bg="white"
                _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => trackAdClick('starbucks', 'https://www.starbucks.com')}
              >
                <VStack spacing={3}>
                  <Box 
                    bg="white" 
                    borderRadius="md" 
                    p={3} 
                    width="200px" 
                    height="200px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image 
                      src="/starbs.png"
                      alt="Starbucks"
                      maxHeight="240px"
                      maxWidth="200px"
                      objectFit="contain"
                    />
                  </Box>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default SignUpSuccess;